import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../lib/utils';

// ============================================
// Card Component - Modern Design System
// ============================================

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'glass' | 'outlined' | 'interactive';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', padding = 'md', hover = false, ...props }, ref) => {
    const baseStyles = 'rounded-xl transition-all duration-200';

    const variantStyles = {
      default: cn(
        'bg-surface border border-border',
        'shadow-[var(--shadow-card)]'
      ),
      elevated: cn(
        'bg-surface border border-border',
        'shadow-[var(--shadow-md)]',
        hover && 'hover:shadow-[var(--shadow-lg)] hover:border-primary/30 hover:-translate-y-0.5'
      ),
      glass: cn(
        'bg-surface/70 backdrop-blur-xl',
        'border border-border/50',
        'shadow-[var(--shadow-lg)]'
      ),
      outlined: cn(
        'bg-transparent border-2 border-border',
        hover && 'hover:border-primary/50 hover:bg-surface/50'
      ),
      interactive: cn(
        'bg-surface border border-border',
        'shadow-[var(--shadow-card)]',
        'cursor-pointer',
        'hover:shadow-[var(--shadow-card-hover)] hover:border-primary/40 hover:-translate-y-0.5',
        'active:scale-[0.99]'
      ),
    };

    const paddingStyles = {
      none: '',
      sm: 'p-3',
      md: 'p-4 md:p-5',
      lg: 'p-5 md:p-6',
    };

    return (
      <div
        ref={ref}
        className={cn(baseStyles, variantStyles[variant], paddingStyles[padding], className)}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

// ============================================
// Card Sub-components
// ============================================

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('px-5 py-4 border-b border-border', className)}
      {...props}
    />
  )
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-lg font-semibold text-text tracking-tight', className)}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-sm text-text-muted mt-1', className)} {...props} />
  )
);
CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-5 py-4', className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('px-5 py-4 border-t border-border flex items-center gap-3', className)}
      {...props}
    />
  )
);
CardFooter.displayName = 'CardFooter';
